import React, { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { observer } from 'mobx-react';
import {
    OrderStyled,
    OrderHeader,
    OrderContentCenter,
    OrderTitle,
    OrderBackdrop,
    OrderShopName,
    OrderAmountWrapper,
    OrderAmountInput,
    OrderLoaderWrapper,
    OrderFooterCopyright,
    OrderError,
    OrderPaymentButtons,
    OrderSbpButton,
    OrderSberPayButton,
} from './Order.styled';
import logo from '../payment/static/logo.svg';
import { OrderStore } from './order.store';
import { Loader } from '../core/icons/Loader';
import { SberPayIcon } from '../core/icons/SberPayIcon';
import { OrderAgreement } from './OrderAgreement.component';
import { createWidget } from '@sber-ecom-core/sberpay-widget';

const AMOUNT_PATTERN = /^\d*[.,]?\d{0,2}$/;
const MAX_AMOUNT = 999999.99;

function validateAmount(value: string, symbol: string): string | null {
    if (!value) return null;
    const num = parseFloat(value.replace(',', '.'));
    if (isNaN(num) || num <= 0) return 'Введите корректную сумму';
    if (num > MAX_AMOUNT) return `Максимальная сумма — ${MAX_AMOUNT.toLocaleString('ru-RU')} ${symbol}`;
    return null;
}

export const Order: FC = observer(() => {
    const [store] = useState(() => new OrderStore());
    const params = useParams();
    const qrCodeId = Number(params.qrCodeId) || 0;
    const [amount, setAmount] = useState('');
    const [amountError, setAmountError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAgreed, setIsAgreed] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (qrCodeId) {
            store.getOrderInfo(qrCodeId);
        }
    }, [store, qrCodeId]);

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const el = e.target;
        const cursorPos = el.selectionStart ?? 0;
        const raw = el.value.replace(/[^\d.,]/g, '');

        if (raw !== '' && !AMOUNT_PATTERN.test(raw)) {
            requestAnimationFrame(() => {
                const pos = Math.min(cursorPos - 1, amount.length);
                el.setSelectionRange(pos, pos);
            });
            return;
        }

        setAmount(raw);
        setAmountError(null);

        requestAnimationFrame(() => {
            const pos = Math.min(cursorPos, raw.length);
            el.setSelectionRange(pos, pos);
        });
    };

    const getValidatedAmount = (): number | null => {
        const error = validateAmount(amount, store.currencySymbol);
        if (error) {
            setAmountError(error);
            return null;
        }
        return parseFloat(amount.replace(',', '.'));
    };

    const handleSbpPay = async () => {
        const numAmount = getValidatedAmount();
        if (numAmount === null) return;

        setIsSubmitting(true);
        const idOrder = await store.createOrder(qrCodeId, numAmount);
        if (idOrder) {
            const nspkLink = await store.payWithSbp(idOrder);
            if (nspkLink) {
                window.location.href = nspkLink;
                return;
            }
        }
        setIsSubmitting(false);
    };

    const handleSberPay = async () => {
        const numAmount = getValidatedAmount();
        if (numAmount === null) return;

        setIsSubmitting(true);
        const idOrder = await store.createOrder(qrCodeId, numAmount);
        if (idOrder) {
            const bankInvoiceId = await store.payWithSberPay(idOrder);
            if (bankInvoiceId) {
                const widget = createWidget('PRODUCTION');
                widget?.open({
                    bankInvoiceId,
                    backUrl: window.location.href,
                }).then(() => {
                    store.isSberPayProcess = false;
                });
                setIsSubmitting(false);
                return;
            }
        }
        store.isSberPayProcess = false;
        setIsSubmitting(false);
    };

    const parsedAmount = parseFloat(amount.replace(',', '.'));
    const isPayDisabled = !amount || isNaN(parsedAmount) || parsedAmount <= 0 || parsedAmount > MAX_AMOUNT || isSubmitting || !isAgreed;

    return (
        <OrderStyled>
            <OrderHeader>
                <img alt="logo" src={logo} width="188px" />
            </OrderHeader>
            <OrderContentCenter>
                <OrderTitle>Оплата</OrderTitle>
                {!store.isLoaded && (
                    <OrderLoaderWrapper>
                        <Loader />
                    </OrderLoaderWrapper>
                )}
                {store.isLoaded && !store.error && (
                    <OrderBackdrop>
                        <OrderShopName>{store.shopName}</OrderShopName>
                        <OrderAmountWrapper>
                            <OrderAmountInput
                                ref={inputRef}
                                type="text"
                                inputMode="decimal"
                                placeholder={store.amountPlaceholder}
                                value={amount ? `${amount} ${store.currencySymbol}` : ''}
                                onChange={handleAmountChange}
                            />
                        </OrderAmountWrapper>
                        {amountError && <OrderError>{amountError}</OrderError>}
                        <OrderAgreement checked={isAgreed} onChange={setIsAgreed} />
                        <OrderPaymentButtons>
                            {store.sbpAvailable && (
                                <OrderSbpButton
                                    disabled={isPayDisabled}
                                    onClick={handleSbpPay}
                                >
                                    {isSubmitting ? 'Обработка...' : <>
                                        Оплатить через
                                        <svg width="20" height="25" viewBox="0 0 35 43" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0.381836 9.44141L5.47868 18.5517V24.1087L0.387798 33.2011L0.381836 9.44141Z" fill="#5B57A2"/>
                                            <path d="M19.9514 15.2361L24.7273 12.3089L34.5015 12.2998L19.9514 21.2133V15.2361Z" fill="#D90751"/>
                                            <path d="M19.9248 9.38774L19.9518 21.4494L14.843 18.3104V0.280273L19.9248 9.38774Z" fill="#FAB718"/>
                                            <path d="M34.5019 12.3002L24.7274 12.3093L19.9248 9.38774L14.843 0.280273L34.5019 12.3002Z" fill="#ED6F26"/>
                                            <path d="M19.9518 33.2517V27.3997L14.843 24.3203L14.8458 42.3683L19.9518 33.2517Z" fill="#63B22F"/>
                                            <path d="M24.7156 30.351L5.47832 18.5517L0.381836 9.44141L34.4811 30.3391L24.7156 30.351Z" fill="#1487C9"/>
                                            <path d="M14.8467 42.3679L19.9519 33.2513L24.7159 30.3508L34.4814 30.3389L14.8467 42.3679Z" fill="#017F36"/>
                                            <path d="M0.387939 33.201L14.8848 24.3204L10.011 21.3301L5.47882 24.1086L0.387939 33.201Z" fill="#984995"/>
                                        </svg>
                                        <b>сбп</b>
                                    </>}
                                </OrderSbpButton>
                            )}
                            {store.sberpayAvailable && (
                                <OrderSberPayButton
                                    disabled={isPayDisabled || store.isSberPayProcess}
                                    onClick={handleSberPay}
                                >
                                    {store.isSberPayProcess ? 'Обработка...' : <>Оплатить с <SberPayIcon/> Pay</>}
                                </OrderSberPayButton>
                            )}
                        </OrderPaymentButtons>
                        {store.isSberPayError && (
                            <OrderError>SberPay недоступен, попробуйте позже</OrderError>
                        )}
                    </OrderBackdrop>
                )}
                {store.error && (
                    <OrderBackdrop>
                        <OrderError>{store.error}</OrderError>
                    </OrderBackdrop>
                )}
                <OrderFooterCopyright>
                    <span>© 2023 Московский филиал Туринвойс Пазарьери Электроник Хизметлер А.Ш.</span><br />
                    <span>ИНН 9909659693 КПП 774791001</span><br />
                    <span>Номер записи об аккредитации в РФ 10230002233 16.11.23 МИФНС47</span><br />
                    <span>123112, РОССИЯ, Г. МОСКВА, ПРЕСНЕНСКАЯ НАБ., Д. 12, ПОМ. 10/45</span><br />
                    <span>Круглосуточно: +7 499 325 4898 +90 850 307 7385</span><br />
                    <span><a href="mailto:yardim@turinvoice.com">yardim@turinvoice.com</a></span><br />
                </OrderFooterCopyright>
            </OrderContentCenter>
        </OrderStyled>
    );
});
